import { zodResolver } from '@hookform/resolvers/zod';
import { ORG_ROLES, type OrgRole, type WorkspaceRole } from '@noconflict/contracts';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { invitationErrorMessage, useCreateInvitation } from '../../../entities/invitation';
import { Button } from '../../../shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../shared/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../shared/ui/form';
import { Input as TextInput } from '../../../shared/ui/input';
import { Notice } from '../../../shared/ui/notice';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../shared/ui/select';
import { toast } from '../../../shared/ui/sonner';
import { GrantRoleFields, type GrantTarget } from '../manage-grants';
import { type InviteInput, type InviteValues, inviteSchema } from './model';

type WorkspaceRef = { id: string; name: string; archived_at: string | null };
/**
 * `org`: the Members tab (`members.manage`): any org role (owner only from an owner) and any workspace access.
 * `workspace`: a workspace manager invites a plain member onto that one workspace (Q31).
 */
export type InviteScope =
  | { kind: 'org'; callerIsOwner: boolean }
  | { kind: 'workspace'; workspace: WorkspaceRef };

const ALL = '*';

/** Invitation form. The database decides who may invite whom; this only offers what can succeed. */
export function InviteMemberDialog({
  open,
  onOpenChange,
  org,
  scope,
  workspaces,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  org: { id: string; name: string };
  scope: InviteScope;
  workspaces: readonly WorkspaceRef[];
}) {
  const { t } = useTranslation('member');
  const create = useCreateInvitation(org.id);
  const [error, setError] = useState<string | null>(null);
  const initialRoles: Record<string, WorkspaceRole> =
    scope.kind === 'workspace' ? { [scope.workspace.id]: 'viewer' } : {};
  const [roles, setRoles] = useState<Record<string, WorkspaceRole>>(initialRoles);
  const form = useForm<InviteInput, unknown, InviteValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: '', orgRole: 'member' },
  });
  const orgRole = form.watch('orgRole');
  const fullAccess = orgRole === 'owner' || orgRole === 'admin';

  const targets: GrantTarget[] =
    scope.kind === 'workspace'
      ? [{ id: scope.workspace.id, name: scope.workspace.name, archived: false }]
      : [
          { id: null, name: t('grants.all'), archived: false },
          // an archived workspace cannot be granted in an invitation
          ...workspaces
            .filter((w) => w.archived_at === null)
            .map((w) => ({ id: w.id, name: w.name, archived: false })),
        ];
  const offeredRoles: readonly OrgRole[] =
    scope.kind === 'org' && scope.callerIsOwner
      ? ORG_ROLES
      : ORG_ROLES.filter((r) => r !== 'owner');

  function close(next: boolean) {
    if (!next) {
      form.reset();
      setRoles(initialRoles);
      setError(null);
    }
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('invite.title', {
              name: scope.kind === 'workspace' ? scope.workspace.name : org.name,
            })}
          </DialogTitle>
          <DialogDescription>{t('invite.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            className="grid gap-4"
            noValidate
            onSubmit={form.handleSubmit(async (values) => {
              setError(null);
              try {
                await create.mutateAsync({
                  email: values.email,
                  orgRole: scope.kind === 'workspace' ? 'member' : values.orgRole,
                  workspaceGrants: fullAccess
                    ? []
                    : targets
                        .filter((target) => roles[target.id ?? ALL])
                        .map((target) => ({
                          workspaceId: target.id,
                          role: roles[target.id ?? ALL] as WorkspaceRole,
                        })),
                });
                toast.success(t('invite.sent'));
                close(false);
              } catch (e) {
                setError(invitationErrorMessage(e));
              }
            })}
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('invite.email')}</FormLabel>
                  <FormControl>
                    <TextInput type="email" autoComplete="off" {...field} />
                  </FormControl>
                  <FormDescription>{t('invite.emailHelp')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {scope.kind === 'org' ? (
              <FormField
                control={form.control}
                name="orgRole"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('columns.role')}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {offeredRoles.map((role) => (
                          <SelectItem key={role} value={role}>
                            {t(`roles.${role}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>{t(`roleHelp.${field.value}`)}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
            {fullAccess ? (
              <Notice>{t('invite.fullAccess')}</Notice>
            ) : (
              <fieldset className="grid gap-2">
                <legend className="mb-2 text-sm font-bold text-heading">
                  {t('columns.access')}
                </legend>
                <GrantRoleFields
                  targets={targets}
                  roleFor={(id) => roles[id ?? ALL] ?? null}
                  onChange={(id, role) =>
                    setRoles((current) => {
                      const next = { ...current };
                      if (role) next[id ?? ALL] = role;
                      else delete next[id ?? ALL];
                      return next;
                    })
                  }
                />
              </fieldset>
            )}
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => close(false)}>
                {t('invite.cancel')}
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {t('invite.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
